import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { organizationQuery } from '@/api/queries';
import type { Organization, Website } from '@/api/types';
import { useSession } from '@/app/session';
import { Button, ConfirmHeader, FieldHint, Icon, Input, KeyValueList, Modal, ModalFooter, ModalHeader, Pill, Segmented, useModalTitleId, useToast } from '@/design-system';
import { domainsQuery as domainSettingsQuery } from '@/features/settings/api';
import { cx } from '@/lib/cx';
import { formatDateShort, formatSum } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import {
  domainSearchQuery,
  domainsKey,
  ordersKey,
  purchaseDomain,
  renewDomain,
  type DomainBudget,
  type DomainOrder,
  type OwnerIn,
  type Quote,
  type SearchResult,
} from './api';
import s from './domains.module.css';

const uuid = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/** The typed value after the PM stops typing (the registrar is asked once per pause). */
function useSettled(value: string, ms = 350): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** «120 000 сум/год» (the price table is in UZS per year, S4 §4). */
export function usePerYear() {
  const { t } = useTranslation('domains');
  return (q: Quote | null) => (q ? t('buy.per_year', { price: formatSum(q.unit_price) }) : t('buy.no_price'));
}

/**
 * «Купить домен» (P7-B01, DESIGN_SPEC §4.5): the search pill with the registrar's name, the rows with
 * «Свободен» / «Занят» and the price from the price table, and «Купить», which opens the owner details
 * (additive: the domain is the client's) and then the design's confirmation. Never automatic.
 */
export function BuyResults({ site, query, setQuery }: { site: Website; query: string; setQuery: (q: string) => void }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const { can } = useSession();
  const settled = useSettled(query.trim().toLowerCase());
  const q = useQuery({ ...domainSearchQuery(site.id, settled), placeholderData: keepPreviousData });
  const perYear = usePerYear();
  const [buying, setBuying] = useState<SearchResult | null>(null);
  // C10: buying is its own permission (TZ §3); without it «Купить» keeps its look and says why.
  const mayBuy = can('domain:purchase');
  const registrar = q.data?.registrar;
  const hint = registrar ? (registrar.operator ? t('buy.registrar_operator', { name: registrar.label ?? registrar.key }) : t('buy.registrar', { name: registrar.label ?? registrar.key })) : t('panel.registrar');
  const err = q.error;
  const off = err instanceof ApiError && err.code === 'registrar_unavailable';
  return (
    <>
      <div className={s.field}>
        <Icon name="search" size={16} className={s.fieldIcon} />
        <input key="search" className={s.input} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('panel.search')} aria-label={t('panel.search')} />
        <span className={s.registrar}>{hint}</span>
      </div>
      {err && <span className={s.connectError}>{off ? t('buy.off') : errorText(err, tc)}</span>}
      <ul className={s.results} aria-busy={q.isFetching}>
        {(err ? [] : (q.data?.results ?? [])).map((r) => {
          const free = r.availability === 'free' || (r.availability === 'unknown' && r.can_buy);
          return (
            <li key={r.domain} className={s.offer}>
              <span className={cx(s.offerDomain, !free && s.offerTaken)}>{r.domain}</span>
              <Pill tone={r.availability === 'free' ? 'ok' : r.availability === 'unknown' ? 'warn' : 'mute'} size="tagTight">
                {t(`buy.availability.${r.availability}`)}
              </Pill>
              <span className={s.offerPrice}>{r.availability === 'taken' ? t('buy.taken_price') : r.availability === 'unsupported' ? t('buy.unsupported_price') : perYear(r.quote)}</span>
              {free && (
                <button
                  type="button"
                  className={s.buy}
                  aria-label={t('panel.buy_label', { domain: r.domain })}
                  aria-disabled={!mayBuy || !r.can_buy || undefined}
                  title={!mayBuy ? t('panel.buy_forbidden') : r.can_buy ? undefined : t('buy.cannot', { domain: r.domain })}
                  onClick={() => (!mayBuy ? toast(t('panel.buy_forbidden')) : r.can_buy ? setBuying(r) : toast(t('buy.cannot', { domain: r.domain })))}
                >
                  {t('panel.buy_action')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <span className={s.flow}>{t('panel.flow')}</span>
      {buying && q.data && <BuyFlow site={site} result={buying} budget={q.data.budget} onClose={() => setBuying(null)} />}
    </>
  );
}

/** «1 200 000» → the PM limit row: the caller's budget, or for an Admin the PM limit from Settings. */
function useBudgetLabel(budget: DomainBudget) {
  const { t } = useTranslation('domains');
  const { isAdmin } = useSession();
  const pmLimit = useQuery({ ...domainSettingsQuery, enabled: isAdmin && budget.limit === null }).data?.values.pm_monthly_budget_uzs;
  if (budget.limit === null) return pmLimit !== undefined ? t('buy.budget_value', { limit: formatSum(pmLimit) }) : t('buy.budget_unlimited');
  if (!budget.spent) return t('buy.budget_value', { limit: formatSum(budget.limit) });
  return t('buy.budget_left', { limit: formatSum(budget.limit), left: formatSum(budget.remaining ?? 0) });
}

type OwnerDraft = {
  kind: OwnerIn['kind'];
  name: string;
  email: string;
  phone: string;
  city: string;
  street: string;
  postal: string;
  tin: string;
  signatory: string;
  bank: string;
  account: string;
  mfo: string;
  passport: string;
  issuedOn: string;
  issuedBy: string;
  pinfl: string;
  whois: boolean;
  authorized: boolean;
};

/** «+998 71 200 44 10» → «+998712004410» (the registry's format). */
function phoneOf(v: string | null | undefined): string {
  const d = (v ?? '').replace(/\D/g, '');
  if (!d) return '';
  return `+${d.startsWith('998') ? d : `998${d}`}`;
}

/** The organization's profile as the owner's first draft: the client is the domain's administrator. */
function draftOf(org: Organization | undefined): OwnerDraft {
  const [city = '', ...rest] = (org?.address ?? '').split(',');
  return {
    kind: org?.tin ? 'legal_entity' : 'individual',
    name: org?.legal_name ?? org?.brand_name ?? '',
    email: org?.email ?? '',
    phone: phoneOf(org?.phone),
    city: city.trim(),
    street: rest.join(',').trim(),
    postal: '',
    tin: (org?.tin ?? '').replace(/\D/g, ''),
    signatory: '',
    bank: '',
    account: '',
    mfo: '',
    passport: '',
    issuedOn: '',
    issuedBy: '',
    pinfl: '',
    whois: false,
    authorized: false,
  };
}

type Problems = Partial<Record<keyof OwnerDraft, string>>;

/** The registry's rules (S4 §3, `owner.py`), checked before anything is sent. */
function check(d: OwnerDraft, t: (k: string) => string): Problems {
  const p: Problems = {};
  if (!d.name.trim()) p.name = t('owner.err.required');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email.trim())) p.email = t('owner.err.email');
  if (!/^\+998\d{9}$/.test(d.phone.trim())) p.phone = t('owner.err.phone');
  if (!d.city.trim()) p.city = t('owner.err.required');
  if (!d.street.trim()) p.street = t('owner.err.required');
  if (d.postal.trim() && !/^\d{6}$/.test(d.postal.trim())) p.postal = t('owner.err.postal');
  if (d.kind === 'legal_entity') {
    if (!/^\d{9}$/.test(d.tin.trim())) p.tin = t('owner.err.tin');
    if (!d.signatory.trim()) p.signatory = t('owner.err.required');
    if (!d.bank.trim()) p.bank = t('owner.err.required');
    if (!/^\d{20}$/.test(d.account.trim())) p.account = t('owner.err.account');
    if (!/^\d{5}$/.test(d.mfo.trim())) p.mfo = t('owner.err.mfo');
  } else {
    if (!/^[A-Z]{2}\d{7}$/.test(d.passport.trim().toUpperCase())) p.passport = t('owner.err.passport');
    if (!d.issuedOn) p.issuedOn = t('owner.err.required');
    if (!d.issuedBy.trim()) p.issuedBy = t('owner.err.required');
    if (!/^\d{14}$/.test(d.pinfl.trim())) p.pinfl = t('owner.err.pinfl');
  }
  if (!d.authorized) p.authorized = t('owner.err.authorized');
  return p;
}

function ownerOf(d: OwnerDraft): OwnerIn {
  return {
    kind: d.kind,
    name: d.name.trim(),
    email: d.email.trim(),
    phone: d.phone.trim(),
    address: { country: 'UZ', city: d.city.trim(), street: d.street.trim(), postal_code: d.postal.trim() || null },
    individual: d.kind === 'individual' ? { passport: d.passport.trim().toUpperCase(), passport_issued_on: d.issuedOn, passport_issued_by: d.issuedBy.trim(), pinfl: d.pinfl.trim() } : null,
    legal_entity: d.kind === 'legal_entity' ? { tin: d.tin.trim(), signatory: d.signatory.trim(), bank: { name: d.bank.trim(), account: d.account.trim(), mfo: d.mfo.trim() } } : null,
    whois_consent: d.whois,
  };
}

/**
 * The buy flow: the owner's details (additive, «данные владельца» of the design's flow note; prefilled
 * from the organization, kept only in this dialog), then the design's «Подтвердите покупку домена» with the
 * domain, the price, the PM budget and what follows. «Купить ‹domain›» is the explicit confirmation.
 */
function BuyFlow({ site, result, budget, onClose }: { site: Website; result: SearchResult; budget: DomainBudget; onClose: () => void }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const tt = t as unknown as (k: string) => string;
  const titleId = useModalTitleId();
  const qc = useQueryClient();
  const toast = useToast();
  const org = useQuery(organizationQuery(site.organization.id)).data;
  const [step, setStep] = useState<'owner' | 'confirm'>('owner');
  const [draft, setDraft] = useState<OwnerDraft>(() => draftOf(org));
  const [touched, setTouched] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // One key per opening: a double click (or a retried request) buys once.
  const key = useRef(uuid());
  const perYear = usePerYear();
  const budgetLabel = useBudgetLabel(budget);
  const filled = useRef(false);
  useEffect(() => {
    if (org && !filled.current) {
      filled.current = true;
      setDraft(draftOf(org));
    }
  }, [org]);
  const problems = touched ? check(draft, tt) : {};
  const set = <K extends keyof OwnerDraft>(k: K, v: OwnerDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const amount = result.quote?.amount ?? 0;
  const over = budget.remaining !== null && amount > budget.remaining;

  const next = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(check(draft, tt)).length) return;
    setStep('confirm');
  };
  const buy = () => {
    if (busy) return;
    if (over) {
      toast(t('buy.over_budget', { left: formatSum(budget.remaining ?? 0) }));
      return;
    }
    setBusy(true);
    setProblem(null);
    purchaseDomain(site.id, key.current, { domain: result.domain, years: 1, owner: ownerOf(draft), client_authorized: true, confirm: true, quoted_amount: amount })
      .then((r) => {
        toast(r.duplicate ? t('buy.duplicate', { domain: result.domain }) : t('buy.started', { domain: result.domain }));
        void qc.invalidateQueries({ queryKey: domainsKey(site.id) });
        void qc.invalidateQueries({ queryKey: ordersKey(site.id) });
        onClose();
      })
      .catch((err: unknown) => setProblem(errorText(err, tc)))
      .finally(() => setBusy(false));
  };

  const field = (k: keyof OwnerDraft, label: string, extra: { mono?: boolean; placeholder?: string; type?: string; inputMode?: 'numeric' | 'email' | 'tel' } = {}) => (
    <div className={s.ownerField}>
      <Input
        label={label}
        value={String(draft[k])}
        onChange={(e) => set(k, e.target.value as never)}
        mono={extra.mono}
        placeholder={extra.placeholder}
        type={extra.type}
        inputMode={extra.inputMode}
        aria-invalid={!!problems[k]}
        data-owner={k}
        autoComplete="off"
      />
      {problems[k] && <span className={s.slugError}>{problems[k]}</span>}
    </div>
  );

  if (step === 'owner')
    return (
      <Modal open onClose={onClose} labelledBy={titleId}>
        <ModalHeader id={titleId} title={t('owner.title')} sub={t('owner.sub', { domain: result.domain })} onClose={onClose} closeLabel={tc('actions.close')} />
        <form onSubmit={next} noValidate className={s.ownerForm}>
          <Segmented
            variant="form"
            label={t('owner.kind')}
            value={draft.kind}
            onChange={(v) => set('kind', v)}
            options={[
              { value: 'legal_entity', label: t('owner.legal_entity') },
              { value: 'individual', label: t('owner.individual') },
            ]}
          />
          {field('name', draft.kind === 'legal_entity' ? t('owner.legal_name') : t('owner.full_name'))}
          <div className={s.ownerGrid}>
            {field('email', t('owner.email'), { type: 'email', inputMode: 'email' })}
            {field('phone', t('owner.phone'), { mono: true, placeholder: '+998901234567', inputMode: 'tel' })}
          </div>
          <div className={s.ownerGrid}>
            {field('city', t('owner.city'))}
            {field('postal', t('owner.postal'), { mono: true, inputMode: 'numeric' })}
          </div>
          {field('street', t('owner.street'))}
          {draft.kind === 'legal_entity' ? (
            <>
              <div className={s.ownerGrid}>
                {field('tin', t('owner.tin'), { mono: true, inputMode: 'numeric' })}
                {field('signatory', t('owner.signatory'))}
              </div>
              {field('bank', t('owner.bank'))}
              <div className={s.ownerGrid}>
                {field('account', t('owner.account'), { mono: true, inputMode: 'numeric' })}
                {field('mfo', t('owner.mfo'), { mono: true, inputMode: 'numeric' })}
              </div>
            </>
          ) : (
            <>
              <div className={s.ownerGrid}>
                {field('passport', t('owner.passport'), { mono: true, placeholder: 'AA1234567' })}
                {field('pinfl', t('owner.pinfl'), { mono: true, inputMode: 'numeric' })}
              </div>
              <div className={s.ownerGrid}>
                {field('issuedOn', t('owner.issued_on'), { type: 'date' })}
                {field('issuedBy', t('owner.issued_by'))}
              </div>
            </>
          )}
          <label className={s.ownerCheck}>
            <input type="checkbox" checked={draft.authorized} onChange={(e) => set('authorized', e.target.checked)} data-owner="authorized" />
            <span>{t('owner.authorized')}</span>
          </label>
          {problems.authorized && <span className={s.slugError}>{problems.authorized}</span>}
          <label className={s.ownerCheck}>
            <input type="checkbox" checked={draft.whois} onChange={(e) => set('whois', e.target.checked)} />
            <span>{t('owner.whois')}</span>
          </label>
          <FieldHint>{t('owner.privacy')}</FieldHint>
          <ModalFooter padTop>
            <Button variant="secondary" size={46} onClick={onClose}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" size={46}>
              {t('owner.next')}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );

  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ConfirmHeader id={titleId} title={t('buy.title')} text={t('buy.text')} />
      <KeyValueList
        rows={[
          { key: 'domain', label: t('buy.domain'), value: result.domain },
          { key: 'price', label: t('buy.price'), value: perYear(result.quote) },
          { key: 'budget', label: t('buy.budget'), value: budgetLabel },
          { key: 'next', label: t('buy.next'), value: t('buy.next_value') },
        ]}
      />
      {(problem || over) && (
        <span className={s.slugError} role="alert">
          {problem ?? t('buy.over_budget', { left: formatSum(budget.remaining ?? 0) })}
        </span>
      )}
      <ModalFooter>
        <Button variant="secondary" size={46} onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button size={46} aria-busy={busy} aria-disabled={over || undefined} onClick={buy}>
          {t('buy.submit', { domain: result.domain })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * The renewal row (P7-A03, additive) in the production card: from 60 days before expiry, «Оплачен до …» with
 * the days left and «Продлить», which asks for the same explicit confirmation as a purchase.
 */
export function RenewRow({ site, order }: { site: Website; order: DomainOrder }) {
  const { t } = useTranslation('domains');
  const [open, setOpen] = useState(false);
  const r = order.renewal;
  if (!r) return null;
  const late = r.window !== 'open';
  return (
    <div className={s.renew}>
      <span className={cx(s.renewText, late && s.renewLate)}>
        {late ? t(`renew.window.${r.window}`, { date: formatDateShort(r.expires_on) }) : t('renew.until', { date: formatDateShort(r.expires_on), count: r.days_left })}
      </span>
      {r.window !== 'closed' && (
        <button type="button" className={s.buy} onClick={() => setOpen(true)}>
          {t('renew.action')}
        </button>
      )}
      {open && <RenewModal site={site} order={order} onClose={() => setOpen(false)} />}
    </div>
  );
}

function RenewModal({ site, order, onClose }: { site: Website; order: DomainOrder; onClose: () => void }) {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation('common');
  const { can } = useSession();
  const titleId = useModalTitleId();
  const qc = useQueryClient();
  const toast = useToast();
  const key = useRef(uuid());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const search = useQuery(domainSearchQuery(site.id, order.domain));
  const found = search.data?.results.find((x) => x.domain === order.domain);
  const quote = found?.renew_quote ?? null;
  const budget = search.data?.budget;
  const perYear = usePerYear();
  const mayBuy = can('domain:purchase');
  const renew = () => {
    if (busy) return;
    if (!mayBuy) {
      toast(t('panel.buy_forbidden'));
      return;
    }
    if (!quote) {
      setProblem(t('buy.no_price'));
      return;
    }
    setBusy(true);
    renewDomain(order.id, key.current, { years: 1, confirm: true, quoted_amount: quote.amount })
      .then(() => {
        toast(t('renew.started', { domain: order.domain }));
        void qc.invalidateQueries({ queryKey: ordersKey(site.id) });
        onClose();
      })
      .catch((err: unknown) => setProblem(errorText(err, tc)))
      .finally(() => setBusy(false));
  };
  return (
    <Modal open onClose={onClose} labelledBy={titleId}>
      <ConfirmHeader id={titleId} title={t('renew.title')} text={t('renew.text')} />
      <KeyValueList
        rows={[
          { key: 'domain', label: t('buy.domain'), value: order.domain },
          { key: 'price', label: t('renew.price'), value: perYear(quote) },
          { key: 'budget', label: t('buy.budget'), value: budget ? <BudgetValue budget={budget} /> : tc('dash') },
          { key: 'until', label: t('renew.new_until'), value: order.renewal ? formatDateShort(nextYear(order.renewal.expires_on)) : tc('dash') },
        ]}
      />
      {problem && (
        <span className={s.slugError} role="alert">
          {problem}
        </span>
      )}
      <ModalFooter>
        <Button variant="secondary" size={46} onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button size={46} aria-busy={busy} aria-disabled={!mayBuy || undefined} title={mayBuy ? undefined : t('panel.buy_forbidden')} onClick={renew}>
          {t('renew.submit', { domain: order.domain })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function BudgetValue({ budget }: { budget: DomainBudget }) {
  return <>{useBudgetLabel(budget)}</>;
}

/** «2026-12-13» → «2027-12-13» (a date, no time zone; 29 February becomes 28 February). */
function nextYear(iso: string): string {
  const y = Number(iso.slice(0, 4)) + 1;
  const md = iso.slice(5) === '02-29' ? '02-28' : iso.slice(5);
  return `${y}-${md}`;
}
